import { usePermissions } from '@ltvco/refresh-lib/ctx';
import { vehiclesTypes } from '@ltvco/refresh-lib/v1';
import { useMutation } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';

import { ERROR_MESSAGES } from '../../constants';
import { fetchVehicleReport, type VehicleValuationParams } from '../../utils';
import {
  buildForecastData,
  extractVehicleDataFromReport,
  extractVehicleSearchParams,
  type VehicleForecastData,
} from '../utils';

interface UseVehicleReportProcessingParams {
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  onProcessingComplete: () => void;
  onProcessingError: () => void;
  fetchedValuation?: vehiclesTypes.AutomobileValuation;
  setValuationParams: (params: VehicleValuationParams) => void;
  setError: (error: string) => void;
}

/**
 * Simple hook to process vehicle reports and generate forecasts
 */
export const useVehicleReportProcessing = ({
  onSendMessage,
  onProcessingComplete,
  onProcessingError,
  fetchedValuation,
  setValuationParams,
  setError,
}: UseVehicleReportProcessingParams) => {
  const { isFreePlan } = usePermissions();
  const [pendingData, setPendingData] = useState<{
    vehicleData: VehicleForecastData;
    cleanMileage: number | undefined;
  } | null>(null);

  // When valuation comes back, finish the process
  useEffect(() => {
    if (!pendingData || !fetchedValuation) return;

    const { vehicleData, cleanMileage } = pendingData;

    if (fetchedValuation.clean_retail?.adjusted) {
      vehicleData.marketValue = fetchedValuation.clean_retail.adjusted;
    }

    // Send forecast or error
    const message = vehicleData.marketValue
      ? buildForecastData(isFreePlan, vehicleData, cleanMileage)
      : ERROR_MESSAGES.NO_FORECAST_AVAILABLE;
    onSendMessage?.(message);
    onProcessingComplete();
    setPendingData(null);
  }, [
    fetchedValuation,
    pendingData,
    onSendMessage,
    onProcessingComplete,
    isFreePlan,
  ]);

  const processVehicleReport = useMutation({
    mutationFn: async ({
      vehicleArgs,
      cleanMileage,
    }: {
      vehicleArgs: Record<string, string>;
      cleanMileage: number | undefined;
    }) => {
      // 1. Get vehicle report
      const vehicleSearchParams = extractVehicleSearchParams(vehicleArgs);
      const reportResult = await fetchVehicleReport(
        vehicleSearchParams,
        isFreePlan,
      );

      if (!reportResult.success || !reportResult.reportData) {
        return { error: ERROR_MESSAGES.TECHNICAL_DIFFICULTIES };
      }

      // 2. Extract vehicle data
      const vehicleData = extractVehicleDataFromReport(reportResult.reportData);
      if (!vehicleData) {
        return { error: ERROR_MESSAGES.NO_VEHICLE_FOUND };
      }

      // 3. Validate we have minimum vehicle data (YMMT)
      if (!vehicleData.year || !vehicleData.make || !vehicleData.model) {
        return { error: ERROR_MESSAGES.INVALID_INPUT };
      }

      // 4. Check if we already have market value from report
      const currentVehicle =
        reportResult.reportData?.entities?.vehicles?.automobiles?.[0];
      const reportMarketValue =
        currentVehicle?.valuation?.clean_retail?.adjusted;

      if (reportMarketValue) {
        vehicleData.marketValue = reportMarketValue;
        const message = buildForecastData(
          isFreePlan,
          vehicleData,
          cleanMileage,
        );
        return { vehicleData, message, skipValuation: true };
      }

      // 5. Use user-provided mileage or vehicle mileage
      const mileage = cleanMileage || vehicleData.mileage || 0;

      // 6. No market value in report, try to get it from valuation API
      const region =
        currentVehicle?.valuation?.region?.name ||
        (typeof vehicleSearchParams.state === 'string'
          ? vehicleSearchParams.state
          : 'NY');

      const valuationParams = {
        year: vehicleData.year.toString(),
        make: vehicleData.make,
        model: vehicleData.model,
        mileage,
        region,
        ...(vehicleSearchParams.vin && { vin: vehicleSearchParams.vin }),
        ...(vehicleSearchParams.licensePlate && {
          plate: vehicleSearchParams.licensePlate,
        }),
        ...(vehicleSearchParams.state && {
          state: vehicleSearchParams.state,
        }),
      };

      return {
        vehicleData,
        cleanMileage,
        valuationParams,
        skipValuation: false,
      };
    },
    onSuccess: (result) => {
      if (result.error) {
        setError(result.error);
        onProcessingError();
      } else if (result.skipValuation) {
        onSendMessage?.(result.message);
        onProcessingComplete();
      } else if (result.valuationParams) {
        setPendingData({
          vehicleData: result.vehicleData,
          cleanMileage: result.cleanMileage,
        });
        setValuationParams(result.valuationParams);
      }
    },
    // NO onError handler - let QueryWrapper's globalErrorHandler handle 426 errors
  });

  return { processVehicleReport, pendingData };
};
